import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { useMyId } from '../hooks/useSession';
import type { Comment } from '../domain/comments';
import { MAX_MESSAGE_LENGTH } from '../domain/conversations';
import { isMe } from '../utils/people';
import CollapsedItem from './CollapsedItem';
import ConfirmDialog from './ConfirmDialog';
import { ActionError } from './PageStates';
import { PersonName } from './PersonDialog';
import ReportDialog from './ReportDialog';
import UserAvatar from './UserAvatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle } from 'lucide-react';
import { ErrorText } from './ErrorText';

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/** A box for a comment or a reply: up to 3,000 characters; the server's refusal shows beside it. */
function CommentBox({
  label,
  submitLabel,
  onSubmit,
  onCancel,
  focusOnOpen = false,
}: {
  label: string;
  submitLabel: string;
  onSubmit: (body: string) => Promise<void>;
  onCancel?: () => void;
  focusOnOpen?: boolean;
}) {
  const [text, setText] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (focusOnOpen) box.current?.focus();
  }, [focusOnOpen]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const body = text.trim();
  const tooLong = body.length > MAX_MESSAGE_LENGTH;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(body);
      setText('');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Textarea
        value={text}
        aria-label={label}
        placeholder={label}
        aria-invalid={tooLong}
        ref={box}
        onChange={(e) => setText(e.target.value)}
        className="min-h-20 rounded-2xl"
      />
      {tooLong && (
        <ErrorText role="alert" className="text-xs">
          Comments can be up to {MAX_MESSAGE_LENGTH.toLocaleString('en-US')} characters.
        </ErrorText>
      )}
      {error !== null && <ActionError error={error} />}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          className="rounded-full"
          disabled={busy || !body || tooLong}
          onClick={submit}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function CommentItem({
  comment,
  listingId,
  isReply = false,
  onChanged,
}: {
  comment: Comment;
  listingId: string;
  isReply?: boolean;
  onChanged: () => void;
}) {
  const { comments, conversations } = useRepositories();
  const navigate = useNavigate();
  const myId = useMyId();
  const [reporting, setReporting] = useState(false);
  const [replying, setReplying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mine = isMe(comment.author, myId);
  const other = !!comment.author && !mine;

  const message = async () => {
    setError(null);
    try {
      const c = await conversations.openDirect(comment.author!.id, listingId);
      navigate(`/conversations/${c.id}`);
    } catch (e) {
      setError(e);
    }
  };

  const remove = async () => {
    setError(null);
    try {
      await comments.delete(comment.id);
      onChanged();
    } catch (e) {
      setError(e);
    }
  };

  const reply = async (body: string) => {
    await comments.post(listingId, { body, replyTo: comment.id });
    setReplying(false);
    onChanged();
  };

  const report = other && (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 rounded-full px-3 text-muted-foreground"
      onClick={() => setReporting(true)}
    >
      Report
    </Button>
  );

  const actions = (
    <div className="-ml-3 mt-1 flex flex-wrap items-center">
      {!isReply && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 rounded-full px-3 text-muted-foreground"
          onClick={() => setReplying(true)}
        >
          Reply
        </Button>
      )}
      {other && !comment.isCollapsed && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 rounded-full px-3 text-muted-foreground"
          onClick={message}
        >
          Message {comment.author!.firstName}
        </Button>
      )}
      {mine && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 rounded-full px-3 text-muted-foreground"
          aria-label="Delete comment"
          onClick={() => setDeleting(true)}
        >
          Delete
        </Button>
      )}
      {report}
    </div>
  );

  const body = (
    <div className="flex items-start gap-3">
      {comment.author ? (
        <UserAvatar person={comment.author} className="size-8" textClassName="text-xs" />
      ) : (
        <div className="size-8 shrink-0 rounded-full bg-muted" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2">
          {comment.author ? (
            <PersonName
              person={comment.author}
              onBlocked={onChanged}
              className="font-semibold text-foreground"
            />
          ) : (
            <span className="font-semibold text-foreground">Deleted account</span>
          )}
          <span className="text-sm text-muted-foreground">
            {formatRelativeTime(comment.createdAt)}
          </span>
        </div>
        <p className="mt-1 whitespace-pre-wrap text-foreground">{comment.body}</p>
        {actions}
      </div>
    </div>
  );

  return (
    <li className={isReply ? 'ml-11' : ''}>
      {comment.isCollapsed ? (
        <CollapsedItem kind="comment" actions={report}>
          {body}
        </CollapsedItem>
      ) : (
        body
      )}
      {error !== null && <ActionError error={error} className="mt-2" />}
      {comment.replies.length > 0 && (
        <ul className="mt-4 space-y-4">
          {comment.replies.map((r) => (
            <CommentItem
              key={r.id}
              comment={r}
              listingId={listingId}
              isReply
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}
      {replying && (
        <div className="ml-11 mt-4">
          <CommentBox
            label="Write a reply"
            submitLabel="Reply"
            focusOnOpen
            onSubmit={reply}
            onCancel={() => setReplying(false)}
          />
        </div>
      )}
      {reporting && (
        <ReportDialog
          target={{ kind: 'comment', id: comment.id }}
          open
          onOpenChange={(open) => !open && setReporting(false)}
        />
      )}
      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setDeleting(false)}
          title="Delete this comment?"
          description={
            <p>
              {isReply
                ? 'It will be gone for everyone.'
                : 'It will be gone for everyone, and so will the replies to it.'}
            </p>
          }
          confirmLabel="Delete"
          onConfirm={remove}
        />
      )}
    </li>
  );
}

/**
 * The comments I can read on a listing, replies nested one level, with a box
 * to add mine (018 FR-026–FR-030). Shown only while the collection has
 * comments on; the server refuses anything else.
 */
export default function CommentSection({ listingId }: { listingId: string }) {
  const repos = useRepositories();
  const comments = useLoad<Comment[]>(() => repos.comments.list(listingId), listingId);

  const post = async (body: string) => {
    await repos.comments.post(listingId, { body, replyTo: null });
    comments.refresh();
  };

  return (
    <section aria-labelledby="comments-heading" className="rounded-3xl bg-card p-6 md:p-8">
      <div className="mb-6 flex items-center gap-2">
        <MessageCircle className="size-5 text-wood" />
        <h2 id="comments-heading" className="text-xl font-semibold text-foreground">
          Comments
        </h2>
      </div>

      {comments.status === 'loading' ? (
        <p role="status" className="text-muted-foreground">
          Loading comments…
        </p>
      ) : comments.status === 'error' ? (
        <p role="alert" className="text-muted-foreground">
          Couldn't load comments.{' '}
          <button className="font-semibold text-primary hover:underline" onClick={comments.reload}>
            Try again
          </button>
        </p>
      ) : comments.data.length === 0 ? (
        <div className="py-4 text-center">
          <p className="font-semibold text-foreground">No comments yet</p>
          <p className="text-sm text-muted-foreground">Be the first to comment.</p>
        </div>
      ) : (
        <ul className="space-y-6">
          {comments.data.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              listingId={listingId}
              onChanged={comments.refresh}
            />
          ))}
        </ul>
      )}

      {comments.status === 'ready' && (
        <div className="mt-6">
          <CommentBox label="Add a comment" submitLabel="Post" onSubmit={post} />
        </div>
      )}
    </section>
  );
}
